import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-row items-center justify-between mb-3 space-x-2"
}
const _hoisted_2 = ["for"]
const _hoisted_3 = { class: "flex flex-row items-center space-x-3" }
const _hoisted_4 = ["id", "min", "max", "step", "disabled", "readonly", "placeholder", "value"]

import { onBeforeMount, ref, useId } from 'vue';
import { ClearButton } from '@/lib/components/Button';
import { BaseIcon } from '@/lib/components/Icon';
import { InputTextHelper } from '@/lib/components/Helper';
import debounce from 'lodash-es/debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'NumberInput',
  props: {
  modelValue: {
    type: [Number, String],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  inputClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  clear: {
    type: Boolean,
    default: false
  },
  successMessage: {
    type: String,
    default: null
  },
  error: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'regular',
    validator: (value: string) => ['xs', 'small', 'regular'].includes(value)
  },
  min: {
    type: Number,
    default: null
  },
  max: {
    type: Number,
    default: null
  },
  step: {
    type: Number,
    default: 1
  },
  decimalPlaces: {
    type: Number,
    default: 0
  },
  hoverBorder: {
    type: Boolean,
    default: true
  },
  fullHeight: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: null
  },
  iconPosition: {
    type: String,
    default: 'left'
  },
  iconColor: {
    type: String,
    default: 'gray-500'
  },
  iconStroke: {
    type: Boolean,
    default: true
  },
  iconHeight: {
    type: String,
    default: '20px'
  }
},
  emits: ["icon-click", "update:modelValue", "blur-changes", "change", "clear"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const inputRef = ref<HTMLInputElement | null>(null);
const id = useId();
const focused = ref(false);
const changeDebounce = debounce(() => emit('change'), 100);

onBeforeMount(() => {
  if (props.modelValue !== '') {
    updateValue();
  }
});

const focus = () => {
  if (!props.disabled && !focused.value) {
    focused.value = true;
    inputRef.value?.focus();
  }
};

const blur = (event: Event) => {
  if (!props.disabled) {
    focused.value = false;
    emit('blur-changes', event);
    updateValue();
  }
};

const updateValue = () => {
  let value = parseFloat(props.modelValue as string);
  if (!isNaN(value)) {
    if (props.min !== null) {
      value = Math.max(value, props.min);
    }
    if (props.max !== null) {
      value = Math.min(value, props.max);
    }
    // Convert the value to a string with the specified decimal places
    const formattedValue = value.toFixed(props.decimalPlaces); // this returns a string
    emit('update:modelValue', formattedValue); // emit the value as a string
    changeDebounce();
  }
};

const onClear = () => {
  emit('update:modelValue', '');
  changeDebounce();
  emit('clear');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (__props.label?.length || _ctx.$slots.action)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("label", {
            for: _unref(id),
            class: _normalizeClass(["inline-block text-base font-medium leading-tight", { 'text-transparent bg-gray-200 rounded-lg': __props.loading }])
          }, _toDisplayString(__props.label), 11, _hoisted_2),
          _createElementVNode("div", _hoisted_3, [
            (_ctx.$slots.action)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass([__props.loading ? 'text-transparent bg-gray-200 rounded-lg' : 'text-primary-500 cursor-pointer', "flex items-center text-sm font-medium text-right"])
                }, [
                  _renderSlot(_ctx.$slots, "action")
                ], 2))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass([{
        'border-danger-700 hover:border-danger-700 focus:border-danger-700 focus:outline-none':
          __props.error && __props.error.length,
        'h-full': __props.fullHeight,
        'hover:border-gray-500': __props.hoverBorder,
        'bg-gray-50 border-transparent': __props.disabled && __props.hoverBorder,
        'bg-gray-50 border-gray-200': __props.readonly && __props.hoverBorder,
        'bg-gray-50': (__props.disabled || __props.readonly) && !__props.hoverBorder,
        'text-gray-600': __props.disabled,
        'border-primary-500': focused.value && !__props.disabled,
        'h-8': __props.size === 'xs',
        'h-10': __props.size === 'small',
        'h-12': __props.size === 'regular',
        inputClass: __props.inputClass
      }, "flex flex-row bg-white text-gray-900 shadow items-center w-full overflow-hidden transition-all duration-100 ease-in-out border rounded focus:shadow-md"])
    }, [
      _renderSlot(_ctx.$slots, "left"),
      (__props.icon?.length && !__props.loading)
        ? (_openBlock(), _createBlock(_unref(BaseIcon), {
            key: 0,
            class: _normalizeClass(['text-' + __props.iconColor, __props.iconPosition === 'right' ? 'order-1' : '']),
            position: __props.iconPosition === 'left' ? 'right' : 'left',
            name: __props.icon,
            height: __props.iconHeight,
            stroke: __props.iconStroke,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('icon-click')))
          }, null, 8, ["class", "position", "name", "height", "stroke"]))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        id: _unref(id),
        ref_key: "inputRef",
        ref: inputRef,
        type: "number",
        min: __props.min,
        max: __props.max,
        step: __props.step,
        disabled: __props.disabled,
        readonly: __props.readonly,
        placeholder: __props.placeholder,
        value: __props.modelValue,
        class: _normalizeClass([[
          __props.disabled || __props.readonly ? 'bg-gray-50 border-gray-200' : 'bg-white',
          __props.disabled ? 'text-gray-600 cursor-default' : 'text-gray-900',
          __props.size === 'xs' ? 'text-xs h-8' : __props.size === 'small' ? 'text-sm h-10' : 'h-12',
          __props.icon ? 'px-3' : 'px-4'
        ], "w-full border-none focus:outline-none"]),
        onInput: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update:modelValue', ($event.target as HTMLInputElement).value))),
        onChange: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(changeDebounce) && _unref(changeDebounce)(...args))),
        onFocus: focus,
        onBlur: blur
      }, null, 42, _hoisted_4),
      (__props.clear && __props.modelValue)
        ? (_openBlock(), _createBlock(_unref(ClearButton), {
            key: 1,
            onClick: _withModifiers(onClear, ["prevent"])
          }))
        : _createCommentVNode("", true)
    ], 2),
    _createVNode(_unref(InputTextHelper), {
      error: __props.error,
      description: __props.successMessage
    }, null, 8, ["error", "description"])
  ]))
}
}

})